import React, { useEffect, useMemo, useState } from 'react';
import {
  BellRing, Bot, ChartNoAxesCombined, Check, Mail, Megaphone,
  Plus, Search, Settings2, ShieldCheck, Users, X,
} from 'lucide-react';
import { api, formatDate, sourceLabel } from './api.js';
import { Header, Modal, SourceGlyph, Spinner, StatusPill, Toggle, useToast } from './components.jsx';

export function Admin() {
  const [user, setUser] = useState(undefined);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const { showToast, Toast } = useToast();

  async function load() {
    const [me, overview] = await Promise.all([api('/api/me'), api('/api/admin/overview')]);
    setUser(me.user);
    setData(overview);
  }

  useEffect(() => {
    api('/api/me')
      .then((me) => {
        setUser(me.user);
        if (!me.user) window.location.assign('/app');
        else if (me.user.isAdmin) return api('/api/admin/overview').then(setData);
        return undefined;
      })
      .catch((error) => showToast(error.message, true));
  }, []);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    window.location.assign('/');
  }

  if (user === undefined || (user?.isAdmin && !data)) return <div className="full-loading"><Spinner label="Counting the mentions…" /></div>;
  if (!user?.isAdmin) {
    return <div className="access-page"><Header user={user} onLogout={logout} /><main><ShieldCheck size={34} /><h1>Admin access only.</h1><p>This dashboard is limited to configured Olamia AI administrators.</p><a className="button" href="/app">Back to my web</a></main><Toast /></div>;
  }

  const users = data.users.filter((item) => item.email.toLowerCase().includes(query.toLowerCase()));
  const metrics = [
    ['Users', data.metrics.users, Users],
    ['Crawler subscribers', data.metrics.subscribers, ChartNoAxesCombined],
    ['Active watches', data.metrics.watches, Search],
    ['Mentions this week', data.metrics.mentions_week, BellRing],
  ];

  return (
    <div className="admin-page">
      <Header user={user} onLogout={logout} />
      <main className="shell admin-main">
        <div className="app-title-row admin-title"><div><span className="eyebrow"><Settings2 size={14} /> Olamia AI admin</span><h1>Spider control.</h1><p>Accounts, source access, delivery and product updates in one place.</p></div><a className="button button-quiet" href="/app">Open my web</a></div>

        <div className="stat-grid admin-stats">
          {metrics.map(([label, value, Icon]) => <article className="stat-card" key={label}><span className="stat-icon"><Icon size={18} /></span><strong>{value}</strong><span>{label}</span></article>)}
        </div>

        <div className="admin-grid">
          <section className="dashboard-card">
            <div className="card-head"><div><h2>Source adoption</h2><p>Selected versus currently entitled accounts.</p></div><StatusPill tone="green">{data.metrics.mentions} total mentions</StatusPill></div>
            <div className="source-admin-list">
              {data.sources.map((source) => (
                <div key={source.source_key}><span className={`mention-source ${source.source_key}`}><SourceGlyph source={source.source_key} size={16} /></span><strong>{source.label}</strong><span>{source.selected_users} selected</span><span>{source.entitled_users} active</span></div>
              ))}
            </div>
          </section>
          <section className="dashboard-card">
            <div className="card-head"><div><h2>Delivery</h2><p>How customers receive mentions.</p></div><BellRing size={21} /></div>
            <div className="delivery-admin-stats">
              <div><Mail size={19} /><strong>{data.delivery.email_enabled}</strong><span>Email enabled</span></div>
              <div><Bot size={19} /><strong>{data.delivery.discord_enabled}</strong><span>Discord enabled</span></div>
              <div><Check size={19} /><strong>{data.delivery.discord_installs}</strong><span>Bot installs</span></div>
            </div>
            <div className="experiment-note"><span>Quick Catch price A/B cohort</span><strong>{data.experiment.key}</strong><p>{data.experiment.variants.map((variant) => `${variant.variant}: ${variant.signups} sign-ups, ${variant.quick_catch_customers} buyers`).join(' · ') || 'No assignments yet'}</p></div>
          </section>
        </div>

        <section className="dashboard-card user-admin-card">
          <div className="card-head admin-user-head"><div><h2>Customer controls</h2><p>Change selected sources, complimentary grants and delivery preferences.</p></div><label className="admin-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search email" /></label></div>
          <div className="admin-user-table">
            <div className="admin-user-row table-head"><span>Customer</span><span>Plan</span><span>Sources</span><span>Usage</span><span /></div>
            {users.map((item) => (
              <div className="admin-user-row" key={item.id}>
                <div><strong>{item.email}</strong><small>Joined {formatDate(item.created_at)}</small></div>
                <div><StatusPill tone={(item.plan === 'scout' || item.complimentary_crawler) ? 'green' : 'neutral'}>{item.complimentary_crawler ? 'Complimentary Crawler' : item.plan === 'scout' ? 'Crawler' : 'Free'}</StatusPill><small>{item.subscription_status} · {Math.max(item.game_limit || 1, item.complimentary_crawler ? item.complimentary_game_limit || 1 : 1)} game limit</small></div>
                <div className="admin-source-chips">{item.selected_sources.map((source) => <span key={source}><SourceGlyph source={source} size={12} />{sourceLabel(source)}</span>)}</div>
                <div><strong>{item.watch_count} watches</strong><small>{item.mention_count} mentions</small></div>
                <button className="button button-quiet button-small" onClick={() => setEditing(item)}>Manage</button>
              </div>
            ))}
            {!users.length && <div className="empty-table">No customers match that email.</div>}
          </div>
        </section>

        <OfferManager catalog={data.catalog || []} onSaved={setData} showToast={showToast} />
        <ProductUpdate showToast={showToast} />
      </main>
      <EditUserModal
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={(nextData) => { setData(nextData); setEditing(null); showToast('Customer preferences updated.'); }}
        showToast={showToast}
      />
      <Toast />
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, showToast }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) {
      setDraft({
        selectedSources: user.selected_sources || ['press'],
        grantedSources: user.granted_sources || [],
        emailEnabled: user.email_enabled,
        emailMode: user.email_mode || 'instant',
        discordEnabled: user.discord_enabled,
        marketingConsent: user.marketing_consent,
        gameLimit: user.game_limit || 1,
        billingCurrency: user.billing_currency || 'USD',
        complimentaryCrawler: user.complimentary_crawler || false,
        complimentaryGameLimit: user.complimentary_game_limit || 1,
      });
    }
  }, [user]);
  if (!user || !draft) return null;

  function toggle(list, key) {
    setDraft((value) => ({
      ...value,
      [list]: value[list].includes(key) ? value[list].filter((item) => item !== key) : [...value[list], key],
    }));
  }
  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: JSON.stringify(draft) });
      onSaved(result);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Manage customer">
      <form className="stack-form admin-edit-form" onSubmit={save}>
        <div className="edit-user-email">{user.email}<StatusPill tone={user.plan === 'scout' ? 'green' : 'neutral'}>{user.plan}</StatusPill></div>
        <fieldset><legend>Selected sources</legend>{['press', 'youtube', 'twitch'].map((source) => <label className="check-row" key={source}><input type="checkbox" checked={draft.selectedSources.includes(source)} disabled={source === 'press'} onChange={() => toggle('selectedSources', source)} /><span><SourceGlyph source={source} size={16} />{sourceLabel(source)}</span></label>)}</fieldset>
        <fieldset><legend>Complimentary source grants</legend>{['youtube', 'twitch'].map((source) => <label className="check-row" key={source}><input type="checkbox" checked={draft.grantedSources.includes(source)} onChange={() => toggle('grantedSources', source)} /><span><SourceGlyph source={source} size={16} />{sourceLabel(source)}</span></label>)}</fieldset>
        <div className="admin-toggle-row"><span>Email delivery</span><Toggle checked={draft.emailEnabled} onChange={(value) => setDraft({ ...draft, emailEnabled: value })} label="Email delivery" /></div>
        <div className="admin-toggle-row"><span>Email cadence</span><StatusPill tone="green">Instant</StatusPill></div>
        <div className="admin-toggle-row"><span>Discord delivery</span><Toggle checked={draft.discordEnabled} onChange={(value) => setDraft({ ...draft, discordEnabled: value })} label="Discord delivery" /></div>
        <div className="admin-toggle-row"><span>Product update consent</span><Toggle checked={draft.marketingConsent} onChange={(value) => setDraft({ ...draft, marketingConsent: value })} label="Marketing consent" /></div>
        <div className="admin-comp-upgrade"><div><strong>Complimentary Crawler</strong><small>Grant paid features without creating a Stripe subscription.</small></div><Toggle checked={draft.complimentaryCrawler} onChange={(value) => setDraft({ ...draft, complimentaryCrawler: value })} label="Complimentary Crawler" /></div>
        {draft.complimentaryCrawler && <label>Complimentary game allowance<input type="number" min="1" max="100" value={draft.complimentaryGameLimit} onChange={(event) => setDraft({ ...draft, complimentaryGameLimit: Number(event.target.value) })} /></label>}
        <label>Game limit<input type="number" min="1" max="100" value={draft.gameLimit} onChange={(event) => setDraft({ ...draft, gameLimit: Number(event.target.value) })} /></label>
        <label>Billing currency<select value={draft.billingCurrency} onChange={(event) => setDraft({ ...draft, billingCurrency: event.target.value })}>{['USD', 'EUR', 'GBP'].map((currency) => <option key={currency}>{currency}</option>)}</select></label>
        <button className="button button-wide" disabled={busy}>{busy ? 'Saving…' : 'Save customer controls'}</button>
      </form>
    </Modal>
  );
}

function OfferManager({ catalog, onSaved, showToast }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');
  useEffect(() => {
    setDrafts(Object.fromEntries(catalog.map((offer) => [offer.product_key, {
      name: offer.name, active: offer.active, perGame: offer.per_game,
      priceUsd: Number(offer.price_usd_cents) / 100, priceEur: Number(offer.price_eur_cents) / 100, priceGbp: Number(offer.price_gbp_cents) / 100,
      ctaLabel: offer.cta_label, featuresText: (offer.features || []).join('\n'),
    }])));
  }, [catalog]);
  function change(key, field, value) { setDrafts((items) => ({ ...items, [key]: { ...items[key], [field]: value } })); }
  async function save(key) {
    setBusy(key);
    try {
      const draft = drafts[key];
      const result = await api(`/api/admin/products/${key}`, { method: 'PATCH', body: JSON.stringify({
        name: draft.name, active: draft.active, perGame: draft.perGame,
        priceUsdCents: Math.round(Number(draft.priceUsd) * 100), priceEurCents: Math.round(Number(draft.priceEur) * 100),
        priceGbpCents: Math.round(Number(draft.priceGbp) * 100), ctaLabel: draft.ctaLabel, features: draft.featuresText.split('\n'),
      }) });
      onSaved(result); showToast('Offer updated across pricing and checkout.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(''); }
  }
  async function create(event) {
    event.preventDefault(); setBusy('new');
    const form = new FormData(event.currentTarget);
    try {
      const result = await api('/api/admin/products', { method: 'POST', body: JSON.stringify({
        productKey: form.get('productKey'), name: form.get('name'), ctaLabel: form.get('ctaLabel'), perGame: form.get('perGame') === 'on',
        priceUsdCents: Math.round(Number(form.get('usd')) * 100), priceEurCents: Math.round(Number(form.get('eur')) * 100),
        priceGbpCents: Math.round(Number(form.get('gbp')) * 100), features: String(form.get('features') || '').split('\n'),
      }) });
      event.currentTarget.reset(); onSaved(result); showToast('New add-on created.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(''); }
  }
  return <section className="dashboard-card offer-manager">
    <div className="card-head"><div><h2>Offers and add-ons</h2><p>Enter normal currency amounts, such as 19 or 29. Changes update customer pricing and new Stripe checkouts.</p></div><StatusPill tone="green">$ · € · £</StatusPill></div>
    <div className="offer-admin-grid">{catalog.map((offer) => { const draft = drafts[offer.product_key]; if (!draft) return null; return <article className="offer-admin-card" key={offer.product_key}>
      <div className="offer-admin-title"><strong>{offer.product_key}</strong><StatusPill tone={offer.kind === 'addon' ? 'orange' : 'green'}>{offer.kind.replace('_', ' ')}</StatusPill></div>
      <label>Name<input value={draft.name} onChange={(event) => change(offer.product_key, 'name', event.target.value)} /></label>
      <div className="offer-price-row">{[['USD', 'priceUsd'], ['EUR', 'priceEur'], ['GBP', 'priceGbp']].map(([currency, field]) => <label key={currency}>{currency}<input type="number" min="0" step="0.01" value={draft[field]} onChange={(event) => change(offer.product_key, field, Number(event.target.value))} /></label>)}</div>
      <label>Button label<input value={draft.ctaLabel} onChange={(event) => change(offer.product_key, 'ctaLabel', event.target.value)} /></label>
      <label>Features, one per line<textarea rows="5" value={draft.featuresText} onChange={(event) => change(offer.product_key, 'featuresText', event.target.value)} /></label>
      <div className="offer-toggles"><label className="check-row"><input type="checkbox" checked={draft.active} onChange={(event) => change(offer.product_key, 'active', event.target.checked)} /><span>Available</span></label><label className="check-row"><input type="checkbox" checked={draft.perGame} onChange={(event) => change(offer.product_key, 'perGame', event.target.checked)} /><span>Per game</span></label></div>
      <button className="button button-small" onClick={() => save(offer.product_key)} disabled={busy === offer.product_key}>{busy === offer.product_key ? 'Saving…' : 'Save offer'}</button>
    </article>; })}</div>
    <form className="new-addon-form" onSubmit={create}><div><h3>Add a new upsell</h3><p>Create an optional monthly feature customers can add to Crawler.</p></div><label>Feature key<input name="productKey" required pattern="[a-z0-9_-]+" placeholder="priority_digest" /></label><label>Name<input name="name" required placeholder="Priority digest" /></label><label>Button label<input name="ctaLabel" required defaultValue="Add feature" /></label><div className="offer-price-row"><label>USD<input name="usd" type="number" min="0" step="0.01" required /></label><label>EUR<input name="eur" type="number" min="0" step="0.01" required /></label><label>GBP<input name="gbp" type="number" min="0" step="0.01" required /></label></div><label className="wide">Features<textarea name="features" rows="3" placeholder="What the customer gets" /></label><label className="check-row"><input name="perGame" type="checkbox" /><span>Charge per game</span></label><button className="button" disabled={busy === 'new'}><Plus size={16} />{busy === 'new' ? 'Creating…' : 'Create add-on'}</button></form>
  </section>;
}

function ProductUpdate({ showToast }) {
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await api('/api/admin/updates', {
        method: 'POST',
        body: JSON.stringify({
          subject: form.get('subject'),
          body: form.get('body'),
          ctaLabel: form.get('ctaLabel'),
          ctaUrl: form.get('ctaUrl'),
        }),
      });
      event.currentTarget.reset();
      showToast('Product update queued for opted-in customers.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  return (
    <section className="dashboard-card update-card">
      <div className="card-head"><div><h2>Send a product update</h2><p>Only customers who explicitly opted in will be queued in n8n.</p></div><Megaphone size={21} /></div>
      <form className="update-form" onSubmit={submit}>
        <label>Subject<input name="subject" required minLength="3" maxLength="120" placeholder="A new strand is ready" /></label>
        <label className="wide">Message<textarea name="body" required minLength="10" rows="4" placeholder="Tell customers what changed and why it helps." /></label>
        <label>Button label <span>optional</span><input name="ctaLabel" maxLength="40" placeholder="Open Press Spider" /></label>
        <label>Button URL <span>optional</span><input name="ctaUrl" type="url" placeholder="https://spider.olamia.app/app" /></label>
        <button className="button" disabled={busy}>{busy ? 'Queuing…' : 'Queue update'}<Megaphone size={16} /></button>
      </form>
    </section>
  );
}

